import React, { useState, useEffect, useRef } from 'react';
import { Upload, Palette, Building2, Phone, Mail, Globe, MapPin, Save, RotateCcw } from 'lucide-react';

// Branding Configuration Panel with logo upload and color pickers
const BrandingPanel = ({ onBrandingChange, initialBranding = {} }) => {
  const [branding, setBranding] = useState(() => ({
    companyName: '',
    logoBase64: '',
    primaryColor: '#1E4D7B',
    accentColor: '#C41E2F',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    ...initialBranding
  }));
  
  const [presets, setPresets] = useState([]);
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [presetName, setPresetName] = useState('');
  const logoInputRef = useRef(null);
  const isInitialized = useRef(false);

  // Load saved presets from localStorage on mount only
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    const savedPresets = localStorage.getItem('geniuslist_branding_presets');
    if (savedPresets) {
      try {
        const parsed = JSON.parse(savedPresets);
        if (parsed && Array.isArray(parsed)) {
          setPresets(parsed);
        }
      } catch (e) {
        console.error('Error loading presets:', e);
      }
    }
    
    // Load last used branding only if no initial branding provided
    if (!initialBranding?.companyName) {
      const lastBranding = localStorage.getItem('geniuslist_last_branding');
      if (lastBranding) {
        try {
          const parsed = JSON.parse(lastBranding);
          if (parsed) {
            setBranding(prev => ({ ...prev, ...parsed }));
          }
        } catch (e) {
          console.error('Error loading last branding:', e);
        }
      }
    }
  }, []); // Empty dependency - run only once on mount

  // Notify parent of branding changes (debounced to prevent render loops)
  const onBrandingChangeRef = useRef(onBrandingChange);
  onBrandingChangeRef.current = onBrandingChange;
  
  useEffect(() => {
    // Use ref to avoid dependency issues
    onBrandingChangeRef.current(branding);
    // Save to localStorage
    localStorage.setItem('geniuslist_last_branding', JSON.stringify(branding));
  }, [branding]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setBranding(prev => ({ ...prev, logoBase64: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const savePreset = () => {
    if (!presetName.trim()) {
      alert('Please enter a preset name');
      return;
    }
    
    const newPreset = {
      id: Date.now(),
      name: presetName.trim(),
      branding: { ...branding }
    };
    
    const updatedPresets = [...presets, newPreset];
    setPresets(updatedPresets);
    localStorage.setItem('geniuslist_branding_presets', JSON.stringify(updatedPresets));
    setPresetName('');
    setShowPresetSave(false);
  };

  const loadPreset = (preset) => {
    setBranding(prev => ({ ...prev, ...preset.branding }));
  };

  const deletePreset = (presetId) => {
    const updatedPresets = presets.filter(p => p.id !== presetId);
    setPresets(updatedPresets);
    localStorage.setItem('geniuslist_branding_presets', JSON.stringify(updatedPresets));
  };

  const resetBranding = () => {
    setBranding({
      companyName: '',
      logoBase64: '',
      primaryColor: '#1E4D7B',
      accentColor: '#C41E2F',
      companyAddress: '',
      companyPhone: '',
      companyEmail: '',
      companyWebsite: ''
    });
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4" data-testid="branding-panel">
      {/* Header with Preset Controls */}
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-600" />
          Custom Branding
        </h4>
        <div className="flex items-center gap-2">
          {presets.length > 0 && (
            <select
              onChange={(e) => {
                const preset = presets.find(p => p.id === parseInt(e.target.value));
                if (preset) loadPreset(preset);
              }}
              className="text-sm px-2 py-1 border border-gray-300 rounded-lg bg-white"
              defaultValue=""
            >
              <option value="" disabled>Load Preset</option>
              {presets.map(preset => (
                <option key={preset.id} value={preset.id}>{preset.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowPresetSave(!showPresetSave)}
            className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
            title="Save as Preset"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={resetBranding}
            className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
            title="Reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Save Preset Input */}
      {showPresetSave && (
        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
          <input
            type="text"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="Enter preset name..."
            className="flex-1 px-3 py-2 border border-purple-200 rounded-lg text-sm text-gray-900 placeholder-gray-400"
          />
          <button
            onClick={savePreset}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
          >
            Save
          </button>
          <button
            onClick={() => setShowPresetSave(false)}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Main Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            value={branding.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            placeholder="Your Real Estate Company"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="branding-company-name"
          />
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Logo
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <button
              onClick={() => logoInputRef.current?.click()}
              className="flex-1 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
              data-testid="branding-logo-upload"
            >
              <Upload className="w-4 h-4" />
              {branding.logoBase64 ? 'Change Logo' : 'Upload Logo'}
            </button>
            {branding.logoBase64 && (
              <div className="w-12 h-12 border border-gray-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                <img
                  src={branding.logoBase64}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
          </div>
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Primary Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.primaryColor}
              onChange={(e) => handleChange('primaryColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
              data-testid="branding-primary-color"
            />
            <input
              type="text"
              value={branding.primaryColor}
              onChange={(e) => handleChange('primaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm text-gray-900 placeholder-gray-400"
              placeholder="#1E4D7B"
            />
          </div>
        </div>

        {/* Accent Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Accent Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.accentColor}
              onChange={(e) => handleChange('accentColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
              data-testid="branding-accent-color"
            />
            <input
              type="text"
              value={branding.accentColor}
              onChange={(e) => handleChange('accentColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm text-gray-900 placeholder-gray-400"
              placeholder="#C41E2F"
            />
          </div>
        </div>
      </div>

      {/* Optional Contact Fields */}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500 mb-3">Optional Contact Information (shown in PDF footer)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={branding.companyAddress}
              onChange={(e) => handleChange('companyAddress', e.target.value)}
              placeholder="123 Main St, City, ST"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={branding.companyPhone}
              onChange={(e) => handleChange('companyPhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="email"
              value={branding.companyEmail}
              onChange={(e) => handleChange('companyEmail', e.target.value)}
              placeholder="agent@example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={branding.companyWebsite}
              onChange={(e) => handleChange('companyWebsite', e.target.value)}
              placeholder="www.example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Color Preview */}
      <div className="flex items-center gap-3 pt-2">
        <span className="text-xs text-gray-500">Preview:</span>
        <div
          className="h-6 w-20 rounded"
          style={{ backgroundColor: branding.primaryColor }}
          title="Primary Color"
        />
        <div
          className="h-6 w-20 rounded"
          style={{ backgroundColor: branding.accentColor }}
          title="Accent Color"
        />
      </div>
    </div>
  );
};

export default BrandingPanel;
